/**
 * Project analysis types — shared contracts for artifact analysis status
 * tracking and description delta produced by the `analyze-artifact` edge function.
 *
 * M1 — SHARED contract consumed by front (M5/M8) and back (M6/M7).
 */

/** Analysis lifecycle for a single artifact. */
export type ArtifactAnalysisStatus = 'pending' | 'analysing' | 'analysed' | 'error';

/**
 * Type guard — checks whether a raw value is a valid ArtifactAnalysisStatus.
 */
export const isArtifactAnalysisStatus = (v: unknown): v is ArtifactAnalysisStatus =>
  v === 'pending' || v === 'analysing' || v === 'analysed' || v === 'error';

/**
 * Delta produced by the `analyze-artifact` edge function and applied to
 * `projects.generated_description` via semantic append.
 */
export type ArtifactAnalysisDelta = {
  /** UUID of the analysed artifact. */
  artifactId: string;
  /**
   * Markdown fragment to semantically append to the project description.
   * Empty string when no delta could be derived (non-parsable source).
   */
  descriptionDelta: string;
  /** Final status after analysis run. */
  status: ArtifactAnalysisStatus;
  /** Human-readable error message when status === 'error', null otherwise. */
  error: string | null;
};

/**
 * Typed crawl failure codes persisted to `project_artifacts.analysis_error`
 * by `POST /api/sources/crawl-link` (M1 — contract frozen in plan.md §5, D7).
 * The front never renders a raw server message for a `link` artifact — only
 * one of these codes, translated via `projects.artifacts.crawlErrors.<code>`.
 */
export type LinkCrawlErrorCode =
  | 'crawl_unreachable'
  | 'crawl_timeout'
  | 'crawl_forbidden_robots'
  | 'crawl_forbidden_host'
  | 'crawl_requires_auth'
  | 'crawl_not_html'
  | 'crawl_empty_content'
  | 'crawl_failed';

const LINK_CRAWL_ERROR_CODES: readonly LinkCrawlErrorCode[] = [
  'crawl_unreachable',
  'crawl_timeout',
  'crawl_forbidden_robots',
  'crawl_forbidden_host',
  'crawl_requires_auth',
  'crawl_not_html',
  'crawl_empty_content',
  'crawl_failed'
];

/**
 * Type guard — checks whether a raw value is a valid LinkCrawlErrorCode.
 * A value that fails this guard (e.g. a raw driver/network message) must
 * fall back to `projects.artifacts.crawlErrors.generic` rather than being
 * rendered verbatim (D7).
 */
export const isLinkCrawlErrorCode = (v: unknown): v is LinkCrawlErrorCode =>
  typeof v === 'string' && (LINK_CRAWL_ERROR_CODES as readonly string[]).includes(v);
